import { ComponentType, CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Visible } from 'coreOld/components';
import { AlignText } from 'coreOld/enums';

import TextLines from './TextLines';

const getMainText = (mainText) => {
  if (Array.isArray(mainText)) {
    return mainText.map((item, index) => <div key={index}>{item}</div>);
  }

  return mainText;
};

const textAlign = {
  [AlignText.LEFT]: 'left',
  [AlignText.RIGHT]: 'right',
  [AlignText.CENTER]: 'center',
};

interface IProps {
  mainText?: string | number | string[] | number[] | JSX.Element | JSX.Element[];
  linkText?: string;
  isLink?: boolean;
  url?: string;
  alignText?: AlignText;
  subTexts?: string[] | JSX.Element[] | (string | JSX.Element)[];
  textEllipsis?: boolean;
  marginBottom?: number;
  mainTextFontSize?: number;
  mainTextEllipsis?: boolean;
  mainTextMaxHeight?: number;
  mainTextBold?: boolean;
  mainTextItalic?: boolean;
  onClick?: () => void;
  wrapperStyle?: CSSProperties;
}

interface LinkProps {
  children?: ReactNode;
  href?: string;
}

export const SubLinkText = styled.a<LinkProps>`
  font-weight: 300;
  font-size: 10px;
  display: block;
  color: inherit;
`;

interface MainLinkTextProps extends LinkProps {
  mainTextFontSize?: number;
  mainTextBold?: boolean;
  mainTextItalic?: boolean;
  target?: string;
}

export const MainLinkText = styled.a<MainLinkTextProps>`
  color: inherit;
`;

interface MainTextProps {
  mainTextFontSize?: number;
  mainTextBold?: boolean;
  mainTextItalic?: boolean;
  mainTextMaxHeight?: number;
  alignText?: AlignText;
  marginBottom?: number;
  style?: any;
  children?: ReactNode;
}

export const MainText = styled.div<MainTextProps>`
  font-weight: 400;

  ${({ mainTextFontSize }) =>
    mainTextFontSize &&
    css`
      font-size: ${mainTextFontSize}px;
    `}

  ${({ mainTextItalic }) =>
    mainTextItalic &&
    css`
      font-style: italic;
    `}

  ${({ mainTextBold }) =>
    mainTextBold &&
    css`
      font-weight: 600;
    `}
`;

interface CellWrapperProps {
  alignText?: AlignText;
  marginBottom?: number;
  style?: any;
  children?: ReactNode;
  onClick?: any;
}

export const CellWrapper = styled.div<CellWrapperProps>`
  white-space: break-spaces !important;

  ${({ alignText }) =>
    alignText &&
    css`
      text-align: ${textAlign[alignText]};
    `}

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${marginBottom}px;
    `}

    ${({ style }) => ({ ...style })}
`;

const CellText = ({
  mainText,
  linkText,
  alignText,
  subTexts,
  textEllipsis = false,
  isLink = false,
  url,
  marginBottom = 0,
  mainTextFontSize = 0,
  mainTextBold = false,
  mainTextItalic = false,
  onClick,
  wrapperStyle,
}: IProps) => {
  const Wrapper: ComponentType<MainTextProps | MainLinkTextProps> = isLink
    ? MainLinkText
    : MainText;
  const linkProps = isLink ? { href: url, target: '_blank' } : {};

  return (
    <CellWrapper
      alignText={alignText}
      marginBottom={marginBottom}
      onClick={onClick}
      style={wrapperStyle}
    >
      <Wrapper
        mainTextFontSize={mainTextFontSize}
        mainTextBold={mainTextBold}
        mainTextItalic={mainTextItalic}
        {...linkProps}
      >
        {getMainText(mainText)}
      </Wrapper>

      <Visible when={Boolean(linkText)}>
        <SubLinkText href={`mailto:${linkText}`}>{linkText}</SubLinkText>
      </Visible>

      {subTexts && <TextLines texts={subTexts} textEllipsis={textEllipsis} isSubText />}
    </CellWrapper>
  );
};

export default CellText;
